// stability-tracker.js - 코너 안정화 상태 추적

/**
 * 안정화 상태
 */
export const StabilityState = {
  NOT_DETECTED: 'not_detected',   // 문서 미감지
  UNSTABLE: 'unstable',           // 감지됨, 불안정
  STABILIZING: 'stabilizing',     // 안정화 진행 중
  STABLE: 'stable'                // 안정적 감지 (캡처 준비)
};

/**
 * 안정화 설정
 * 코너가 감지되면 이미 안정적으로 들고 있는 상태이므로 빠르게 캡처
 */
const DEFAULT_CONFIG = {
  bufferSize: 10,                 // 프레임 버퍼 크기
  stableFramesRequired: 2,        // 안정 상태로 인정되는 연속 프레임 수 (~130ms at 15fps)
  stabilizingFramesRequired: 1,   // stabilizing 상태 최소 프레임 수
  maxMovementThreshold: 35,       // 최대 허용 이동량 (픽셀) - 더 여유있게
  minConfidence: 0.35,            // 최소 신뢰도
  stateDebounceFrames: 2          // 상태 전환 전 필요한 연속 프레임 수 (플리커 방지)
};

/**
 * 두 코너 세트 간의 최대 이동량 계산
 * @param {Object} corners1 - {tl, tr, br, bl}
 * @param {Object} corners2 - {tl, tr, br, bl}
 * @returns {number} 최대 이동량 (픽셀)
 */
function calculateMaxMovement(corners1, corners2) {
  if (!corners1 || !corners2) return Infinity;

  const keys = ['tl', 'tr', 'br', 'bl'];
  let maxDist = 0;

  for (const key of keys) {
    const p1 = corners1[key];
    const p2 = corners2[key];
    if (!p1 || !p2) return Infinity;

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    maxDist = Math.max(maxDist, dist);
  }

  return maxDist;
}

/**
 * 코너 안정화 추적기
 */
export class StabilityTracker {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.reset();

    // 콜백
    this.onStabilityChange = null;
    this.onStable = null;
  }

  /**
   * 상태 리셋
   */
  reset() {
    this.frameBuffer = [];
    this.consecutiveStableFrames = 0;
    this.currentState = StabilityState.NOT_DETECTED;
    this.lastCorners = null;
    this.lastConfidence = 0;
    // 상태 디바운스용
    this.pendingState = null;
    this.pendingStateFrames = 0;
  }

  /**
   * 새 프레임 추가
   * @param {Object|null} corners - 감지된 코너 또는 null (미감지)
   * @param {number} confidence - 신뢰도 (0~1)
   * @returns {string} 현재 안정화 상태
   */
  addFrame(corners, confidence = 1) {
    const prevState = this.currentState;

    // 문서 미감지 - 디바운스 적용 (순간적인 감지 실패 무시)
    if (!corners || confidence < this.config.minConfidence) {
      // NOT_DETECTED로 전환도 디바운스
      if (this.pendingState === StabilityState.NOT_DETECTED) {
        this.pendingStateFrames++;
        if (this.pendingStateFrames >= this.config.stateDebounceFrames) {
          this.frameBuffer = [];
          this.consecutiveStableFrames = 0;
          this.currentState = StabilityState.NOT_DETECTED;
          this.lastCorners = null;
          this.lastConfidence = 0;
          this.pendingState = null;
          this.pendingStateFrames = 0;

          if (prevState !== this.currentState && this.onStabilityChange) {
            this.onStabilityChange(this.currentState, null);
          }
        }
      } else {
        this.pendingState = StabilityState.NOT_DETECTED;
        this.pendingStateFrames = 1;
      }
      return this.currentState;
    }

    // 감지 성공 시 NOT_DETECTED pending 리셋
    if (this.pendingState === StabilityState.NOT_DETECTED) {
      this.pendingState = null;
      this.pendingStateFrames = 0;
    }

    // 버퍼에 추가
    this.frameBuffer.push({ corners, confidence, timestamp: Date.now() });

    // 버퍼 크기 제한
    if (this.frameBuffer.length > this.config.bufferSize) {
      this.frameBuffer.shift();
    }

    this.lastCorners = corners;
    this.lastConfidence = confidence;

    // 이전 프레임과 비교하여 이동량 계산
    if (this.frameBuffer.length >= 2) {
      const prev = this.frameBuffer[this.frameBuffer.length - 2].corners;
      const movement = calculateMaxMovement(prev, corners);

      if (movement <= this.config.maxMovementThreshold) {
        this.consecutiveStableFrames++;
      } else {
        // 큰 이동 발생 시 리셋
        this.consecutiveStableFrames = 1;
      }
    } else {
      this.consecutiveStableFrames = 1;
    }

    // 상태 결정 (내부 계산용)
    let calculatedState;
    if (this.consecutiveStableFrames >= this.config.stableFramesRequired) {
      calculatedState = StabilityState.STABLE;
    } else if (this.consecutiveStableFrames >= this.config.stabilizingFramesRequired) {
      calculatedState = StabilityState.STABILIZING;
    } else {
      calculatedState = StabilityState.UNSTABLE;
    }

    // 디바운스: 상태 전환 시 몇 프레임 연속으로 같아야 실제 전환
    // STABLE로 가는 건 디바운스 없이 즉시 (캡처 지연 방지)
    if (calculatedState === StabilityState.STABLE) {
      this.currentState = StabilityState.STABLE;
      this.pendingState = null;
      this.pendingStateFrames = 0;
    } else if (calculatedState !== this.currentState) {
      // 다른 상태로 전환 시도
      if (this.pendingState === calculatedState) {
        this.pendingStateFrames++;
        if (this.pendingStateFrames >= this.config.stateDebounceFrames) {
          this.currentState = calculatedState;
          this.pendingState = null;
          this.pendingStateFrames = 0;
        }
      } else {
        // 새로운 pending 상태 시작
        this.pendingState = calculatedState;
        this.pendingStateFrames = 1;
      }
    } else {
      // 현재 상태 유지 - pending 리셋
      this.pendingState = null;
      this.pendingStateFrames = 0;
    }

    // 상태 변경 콜백
    if (prevState !== this.currentState && this.onStabilityChange) {
      this.onStabilityChange(this.currentState, corners);
    }

    // 안정 상태 도달 콜백 (최초 1회)
    if (this.currentState === StabilityState.STABLE &&
        prevState !== StabilityState.STABLE &&
        this.onStable) {
      this.onStable(corners, confidence);
    }

    return this.currentState;
  }

  /**
   * 현재 상태 조회
   * @returns {string}
   */
  getState() {
    return this.currentState;
  }

  /**
   * 마지막 코너 조회
   * @returns {Object|null}
   */
  getLastCorners() {
    return this.lastCorners;
  }

  /**
   * 안정화 진행률 (0~1)
   * @returns {number}
   */
  getProgress() {
    if (this.currentState === StabilityState.NOT_DETECTED) {
      return 0;
    }
    return Math.min(1, this.consecutiveStableFrames / this.config.stableFramesRequired);
  }

  /**
   * 평균 코너 위치 (스무딩용)
   * @param {number} windowSize - 평균 계산에 사용할 프레임 수
   * @returns {Object|null}
   */
  getSmoothedCorners(windowSize = 3) {
    if (this.frameBuffer.length === 0) return null;

    const recentFrames = this.frameBuffer.slice(-windowSize);
    if (recentFrames.length === 0) return null;

    const avg = { tl: { x: 0, y: 0 }, tr: { x: 0, y: 0 }, br: { x: 0, y: 0 }, bl: { x: 0, y: 0 } };
    const keys = ['tl', 'tr', 'br', 'bl'];

    for (const frame of recentFrames) {
      for (const key of keys) {
        avg[key].x += frame.corners[key].x;
        avg[key].y += frame.corners[key].y;
      }
    }

    const count = recentFrames.length;
    for (const key of keys) {
      avg[key].x = Math.round(avg[key].x / count);
      avg[key].y = Math.round(avg[key].y / count);
    }

    return avg;
  }
}
